import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { UpdateRounded } from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';
import { KeyboardDatePicker } from '../../components/CustomDatePicker';
import { unwrapResult } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { ProjectDocumentRevision } from '../../api/project-document-revisions';
import { RootState } from '../../app/store';
import DocumentInfoCard from '../../components/DocumentInfoCard';
import HelpTooltip from '../../components/HelpTooltip';
import ProjectInfoCard from '../../components/ProjectInfoCard';
import { DAYS_FOR_COMPLETE_REVIEW } from '../../constants';
import { addWeekdays, countWeekdays } from '../../functions/addWeekdays';
import { useAppDispatch } from '../../hooks/useAppRudux';
import useMasterDateForDocumentRevision from '../../hooks/useMasterDateForDocumentRevision';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import { Purpose } from '../MasterData/Purpose/purposeSlice';
import { Revision } from '../MasterData/Revision/revisionSlice';
import { PurposeLabel } from '../ProjectDocument/BulkPlanning';
import {
  getProjectDocumentRevisionAsync,
  updateProjectDocumentRevisionAsync
} from './projectDocumentRevisionSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    hoverFocus: {
      border: '2px solid',
      borderColor: theme.palette.background.default,
      borderRadius: 16,
      transition: '0.4s',
      '&:hover': {
        borderColor: theme.palette.primary.main
      }
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    fullWidth: {
      width: '100%'
    },
    padding1: {
      padding: theme.spacing(1)
    },
    marginTopNone: {
      marginTop: 0
    },
    marginRightNone: {
      marginRight: 0
    },
    marginBottom: {
      marginBottom: theme.spacing(2)
    },
    showInformation: {
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    }
  })
);

interface ProjectDocumentRevisionWithId extends ProjectDocumentRevision {
  id: number | null;
}

export default function DocumentRevisionEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const params = useParams<{ revId: string }>();
  const revId = Number.parseInt(params.revId, 10);

  const { selectedProjectDocumentRevision } = useSelector(
    (state: RootState) => state.projectDocumentRevisions
  );
  const { selectedProjectDocument } = useSelector(
    (state: RootState) => state.projectDocuments
  );
  const { selectedProject } = useSelector((state: RootState) => state.projects);

  const [purposes] = useMasterDateForDocumentRevision();

  const [revUpdate, setRevUpdate] = useState<ProjectDocumentRevisionWithId>({
    id: null,
    purpose: null,
    revision: null,
    manHour: null,
    planIssueDate: new Date(),
    planCompletedDate: new Date(), //TODO: add 20 working days function
    isAutoApproval: false,
    projectDocumentId: null
  });

  const currentRevision = purposes.find(
    (purpose) => purpose.id === revUpdate.purpose?.id
  )?.revisions;
  const revisionList = currentRevision ? currentRevision : [];
  const [daysForCompleteReview, setDaysForCompleteReview] = useState<number>(
    DAYS_FOR_COMPLETE_REVIEW
  );
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchCurrentDocRevision = () => {
      if (selectedProjectDocumentRevision) {
        const daysForCompleteReview = countWeekdays(
          selectedProjectDocumentRevision.planIssueDate,
          selectedProjectDocumentRevision.planCompletedDate
        );
        setDaysForCompleteReview(daysForCompleteReview);
        const currentRevisions = purposes.find(
          (purpose) => purpose.id === selectedProjectDocumentRevision.purposeId
        )?.revisions;
        const revisions = currentRevisions ? currentRevisions : [];
        setRevUpdate({
          ...selectedProjectDocumentRevision,
          purpose: purposes.find(
            (purpose) =>
              purpose.id === selectedProjectDocumentRevision.purposeId
          ) as Purpose,
          revision: revisions.find(
            (revision) =>
              revision.id === selectedProjectDocumentRevision.revisionId
          ) as Revision,
          isAutoApproval: Boolean(
            selectedProjectDocumentRevision.isAutoApproval
          )
        });
      }
    };
    if (purposes.length > 0) {
      fetchCurrentDocRevision();
    }
  }, [purposes, selectedProjectDocumentRevision]);

  useEffect(() => {
    dispatch(getProjectDocumentRevisionAsync(revId));
  }, []);

  async function onSubmitDocumentRevision(
    event: React.FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();
    try {
      setLoading(true);
      const result = await dispatch(
        updateProjectDocumentRevisionAsync(revUpdate)
      );
      await unwrapResult(result);
      dispatch(
        loadingSlice.actions.success({
          message: t('documentRevisionEdit.messages.revisionUpdated')
        })
      );
      setTimeout(() => {
        history.push(`/documents/${revUpdate.projectDocumentId}`);
      }, 1000);
    } catch (error) {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="editRevision"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id,
          documentName: selectedProjectDocument?.name,
          documentId: selectedProjectDocument?.id,
          revisionName: `Purpose: ${selectedProjectDocumentRevision?.purpose.abbreviation}, Revision: ${selectedProjectDocumentRevision?.revision.name}`,
          revisionId: selectedProjectDocumentRevision?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('documentRevisionEdit.title')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} className={classes.showInformation}>
          {selectedProject && <ProjectInfoCard project={selectedProject} />}
          {selectedProjectDocument && (
            <DocumentInfoCard document={selectedProjectDocument} />
          )}
        </Grid>
        <Grid item xs={12} md={9}>
          <form onSubmit={onSubmitDocumentRevision}>
            <Paper className={classes.card}>
              <Typography variant="subtitle1" gutterBottom>
                {t('documentRevisionEdit.subtitle')}
              </Typography>
              <Divider className={classes.divider} />
              <Alert className={classes.marginBottom} severity="info">
                {t('documentRevisionEdit.autoCalculateMessage', { days: daysForCompleteReview })}
              </Alert>
              <Grid
                className={clsx(classes.padding1, classes.hoverFocus)}
                container
                spacing={1}
                component={Paper}
                variant="outlined"
              >
                <Grid item xs={7}>
                  <Autocomplete
                    className={classes.fullWidth}
                    id="purpose"
                    options={purposes}
                    getOptionLabel={(option) =>
                      `${option.abbreviation}: ${option.fullName}`
                    }
                    value={revUpdate?.purpose}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, newValue) =>
                      setRevUpdate({
                        ...revUpdate,
                        purpose: newValue,
                        revision: null,
                        isAutoApproval: false
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label={t('documentRevisionEdit.labels.purpose')}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={5}>
                  <Autocomplete
                    className={classes.fullWidth}
                    id="revision"
                    options={revisionList}
                    getOptionLabel={(option) => option?.name}
                    value={revUpdate?.revision}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, newValue) =>
                      setRevUpdate({
                        ...revUpdate,
                        revision: newValue
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label={t('documentRevisionEdit.labels.revision')}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    className={classes.fullWidth}
                    type="number"
                    id="man-hour"
                    size="small"
                    label={t('documentRevisionEdit.labels.manHour')}
                    required
                    value={revUpdate.manHour ? revUpdate.manHour : ''}
                    onChange={(event) =>
                      setRevUpdate({
                        ...revUpdate,
                        manHour:
                          event.target.value === '' ||
                          Number.parseFloat(event.target.value) <= 0
                            ? null
                            : Number.parseFloat(event.target.value)
                      })
                    }
                  />
                </Grid>
                <Grid item xs={7}>
                  <KeyboardDatePicker
                    className={clsx(classes.fullWidth, classes.marginTopNone)}
                    autoOk
                    size="small"
                    variant="inline"
                    InputAdornmentProps={{ position: 'end' }}
                    id="issue-date"
                    label="Issue Date"
                    value={revUpdate.planIssueDate}
                    onChange={(date) =>
                      setRevUpdate({
                        ...revUpdate,
                        planIssueDate: date as Date,
                        planCompletedDate: addWeekdays(
                          date as Date,
                          daysForCompleteReview
                        )
                      })
                    }
                    KeyboardButtonProps={{
                      'aria-label': t('documentRevisionEdit.ariaLabels.changeDate') as string
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <KeyboardDatePicker
                    className={clsx(classes.fullWidth, classes.marginTopNone)}
                    autoOk
                    size="small"
                    variant="inline"
                    InputAdornmentProps={{ position: 'end' }}
                    id="completed-date"
                    label="Completed Date"
                    value={revUpdate.planCompletedDate}
                    onChange={(date) =>
                      setRevUpdate({
                        ...revUpdate,
                        planCompletedDate: date as Date
                      })
                    }
                    KeyboardButtonProps={{
                      'aria-label': t('documentRevisionEdit.ariaLabels.changeDate') as string
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    className={classes.marginRightNone}
                    control={
                      <Checkbox
                        size="small"
                        color="primary"
                        disabled={revUpdate.purpose?.id !== PurposeLabel.IFR}
                        checked={revUpdate.isAutoApproval}
                        onChange={(event) =>
                          setRevUpdate({
                            ...revUpdate,
                            isAutoApproval: event.target.checked
                          })
                        }
                      />
                    }
                    label={t('documentRevisionEdit.labels.autoApproval')}
                  />
                  <HelpTooltip>
                    {revUpdate.purpose?.id === PurposeLabel.IFR
                      ? t('documentRevisionEdit.helpText.autoApprovalEnabled')
                      : t('documentRevisionEdit.helpText.autoApprovalIfrOnly')}
                  </HelpTooltip>
                </Grid>
              </Grid>
            </Paper>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={loading}
              startIcon={<UpdateRounded />}
            >
              {t('documentRevisionEdit.actions.updateRevision')}
            </Button>
          </form>
        </Grid>
      </Grid>
    </div>
  );
}
